import { createRelations, type Chain } from './layout';
import { PlacementRuleType } from '../../../api/generated';
import type { EditorNode } from './types';

// A cross-row link drawn as a diagonal line. Inline neighbours on the same row
// are joined by the horizontal Connector instead.
export interface BranchConnection {
  fromId: string;
  toId: string;
}

interface ChainPosition {
  order: number;
  index: number;
  headId: string;
}

function buildPositionMap(chains: Chain[]): Map<string, ChainPosition> {
  const position = new Map<string, ChainPosition>();
  chains.forEach((chain, order) => {
    const headId = chain.items[0]?.id ?? '';
    chain.items.forEach((item, index) => position.set(item.id, { order, index, headId }));
  });
  return position;
}

// The link to draw for one dependency edge, or undefined when the pair is
// already joined inline (consecutive on the same row) or has no drawable
// endpoints.
function connectionForEdge(
  node: EditorNode,
  successor: EditorNode,
  position: Map<string, ChainPosition>
): BranchConnection | undefined {
  const from = position.get(node.id);
  const to = position.get(successor.id);
  if (!from || !to) return undefined;
  // Inline neighbours on the same row already have a horizontal connector.
  if (from.order === to.order && to.index === from.index + 1) return undefined;

  // The successor's row is a branch entered from above: meet it at its head
  // so the line doesn't cross that row's earlier nodes. A predecessor on a
  // lower row keeps its actual node (it flows outward, not in).
  const toId = to.order > from.order ? to.headId : successor.id;
  if (node.id === toId) return undefined;
  return { fromId: node.id, toId };
}

// A LAST intervention closes every open branch: draw a line from each branch
// (a chain that doesn't contain the LAST node) into it, so nothing dangles.
function pushLastNodeConnections(
  nodes: EditorNode[],
  chains: Chain[],
  push: (connection: BranchConnection) => void
): void {
  const lastNode = nodes.find((n) => n.dependencies.some((d) => d.type === PlacementRuleType.Last));
  if (!lastNode) return;
  for (const chain of chains) {
    if (chain.items.some((item) => item.id === lastNode.id)) continue;
    const tail = chain.items[chain.items.length - 1];
    if (!tail) continue;
    push({ fromId: tail.id, toId: lastNode.id });
  }
}

// Draw a link for every dependency edge that isn't an inline consecutive pair.
// A "before" rule is just an "after" rule seen from the other side, so this
// covers both directions. When the successor lands on a lower row, that row is
// a branch entered from above, so the line meets it at its head rather than
// cutting across its earlier nodes. `chains` must be in render order.
export function computeBranchConnections(
  nodes: EditorNode[],
  chains: Chain[]
): BranchConnection[] {
  const { getSuccessors } = createRelations(nodes);
  const position = buildPositionMap(chains);

  const connections: BranchConnection[] = [];
  const seen = new Set<string>();
  const push = (connection: BranchConnection): void => {
    const key = `${connection.fromId}->${connection.toId}`;
    if (seen.has(key)) return;
    seen.add(key);
    connections.push(connection);
  };

  for (const node of nodes) {
    for (const successor of getSuccessors(node.id)) {
      const connection = connectionForEdge(node, successor, position);
      if (connection) push(connection);
    }
  }

  pushLastNodeConnections(nodes, chains, push);

  return connections;
}
